/**
 * DeliverySidebar Component
 * Simplified sidebar for Delivery users - shows only Jobs link
 * Mobile-optimized design
 */

import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/redux';
import { logout as logoutAction } from '../../store/slices/authSlice';
import { useLogoutMutation } from '../../store/slices/apiSlice';
import { Briefcase, LogOut, Menu, X } from 'lucide-react';

interface DeliverySidebarProps {
  isMobileMenuOpen?: boolean;
  onToggleMobileMenu?: () => void;
}

export const DeliverySidebar: React.FC<DeliverySidebarProps> = ({
  isMobileMenuOpen = false,
  onToggleMobileMenu
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [triggerLogout, { isLoading: isLoggingOut }] = useLogoutMutation();

  const isItemActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const onLogout = async () => {
    try {
      await triggerLogout({ refresh: '' }).unwrap().catch(() => undefined);
    } finally {
      dispatch(logoutAction());
      window.location.href = '/login';
    }
  };

  // Mobile hamburger menu for small screens
  const MobileHeader = () => (
    <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
          <Briefcase className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="font-bold text-lg text-gray-900">Glassshop</h1>
          <p className="text-xs text-gray-500">Delivery Portal</p>
        </div>
      </div>
      <button
        onClick={onToggleMobileMenu}
        className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>
    </div>
  );

  // Sidebar content
  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo - Desktop only */}
      <div className="hidden lg:flex items-center gap-3 px-4 py-4 border-b border-gray-200">
        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
          <Briefcase className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="font-bold text-lg text-gray-900">Glassshop</h1>
          <p className="text-xs text-gray-500">Delivery Portal</p>
        </div>
      </div>

      {/* Navigation - Single Jobs Link */}
      <nav className="flex-1 px-3 py-4 space-y-2">
        <NavLink
          to="/jobs"
          className={`
            flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
            ${isItemActive('/jobs')
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' 
              : 'text-gray-700 hover:bg-gray-100'
            }
          `}
        >
          <Briefcase className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium">My Jobs</span>
        </NavLink>
      </nav>

      {/* Footer - Logout */}
      <div className="p-3 border-t border-gray-200">
        <button
          onClick={onLogout}
          disabled={isLoggingOut}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium">{isLoggingOut ? 'Logging out...' : 'Log out'}</span>
        </button>
      </div>
    </div>
  );;

  return (
    <>
      {/* Mobile Header */}
      <MobileHeader />

      {/* Sidebar - Desktop always visible, Mobile conditional */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:static lg:h-screen
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={onToggleMobileMenu}
        />
      )}
    </>
  );
};

DeliverySidebar.displayName = 'DeliverySidebar';
